import { useEffect, useState } from 'react';
import { getTraining } from '../api/getTraining';
import { getCourse } from '../api/getCourse';
import { CourseResponse } from '../models/courseResponse';
import trainingService from '../../../services/trainingService';
import { getPathway } from '../api/getPathway';
import { PathwayResponse } from '../models/pathwayResponse';
import { Training } from '../models/training';
import { TrainingCourse } from '../models/trainingCourse';
import { TrainingPathway } from '../models/trainingPathway';
import compilationService from '../../../services/compilationService';
import { getNextAction } from '../api/getNextAction';
import { NextAction } from '../models/nextAction';
import { Compilation } from '../models/compilation';

export function transformTraining(
  searchData: Training,
  trainingData: PathwayResponse | CourseResponse,
  type: 'course' | 'pathway'
): Training {
  const itemNumber = searchData.itemNumber || '';

  return type === 'course'
    ? ({
        ...searchData,
        ...trainingData,
        type: 'course',
        itemNumber,
        isIncludedWithGridSubscription: trainingService.isIncludedWithGridSubscription(itemNumber),
      } as TrainingCourse)
    : ({
        ...searchData,
        ...trainingData,
        type: 'pathway',
        itemNumber,
        isIncludedWithGridSubscription: trainingService.isIncludedWithGridSubscription(itemNumber),
      } as TrainingPathway);
}

export const useTraining = (trainingId: string, compilationId?: string) => {
  const [data, setData] = useState<{
    training?: Training;
    isLoading: boolean;
    error?: boolean;
  }>({
    isLoading: true,
  });
  const [compilation, setCompilation] = useState<Compilation>();
  const [nextAction, setNextAction] = useState<NextAction>();

  useEffect(() => {
    getTraining(trainingId)
      .then(training => {
        if (training.trainingType === 'Tasklist') {
          getCourse(trainingId)
            .then(tasklist => {
              if (tasklist && Object.keys(tasklist).length !== 0) {
                setData({
                  ...data,
                  training: transformTraining(training, tasklist, 'course'),
                  isLoading: false,
                });
              } else {
                setData({ isLoading: false, error: true });
              }
            })
            .catch(() => {
              setData({ isLoading: false, error: true });
            });
        } else {
          getPathway(trainingId)
            .then(workflow => {
              if (workflow && Object.keys(workflow).length !== 0) {
                setData({
                  ...data,
                  training: transformTraining(training, workflow, 'pathway'),
                  isLoading: false,
                });
              } else {
                setData({ isLoading: false, error: true });
              }
            })
            .catch(() => {
              setData({ isLoading: false, error: true });
            });
        }
        getNextAction(training.trainingType, trainingId).then(setNextAction);
      })
      .catch(() => {
        setData({ isLoading: false, error: true });
      });
  }, [trainingId]);

  useEffect(() => {
    if (compilationId) {
      compilationService.getCompilation(compilationId).then(compilation => {
        setCompilation(compilation);
      });
    }
  }, [compilationId]);

  return {
    data,
    compilation,
    nextAction,
  };
};
